import type React from 'react';
import { useTranslation } from 'react-i18next';

import Info from '@/assets/icons/info.svg?component';
import SkeletonBox from '@/components/atoms/SkeletonBox/SkeletonBox';
import TitleIcon from '@/components/atoms/TitleIcon/TitleIcon';
import { ChartHeaderActions } from '@/components/charts/actions';
import { MetricChart, type UseMetricChartResult } from '@/components/charts/generic';
import ContentBlock from '@/components/molecules/ContentBlock';
import type { DateRange } from '@/components/molecules/DateRangePicker/DateRangePicker';
import type { RetryProps } from '@/components/retryFailedQueries';
import type { ThresholdRange } from '@/domain/behavior';
import GridLayout from '@/routes/Client/Cage/pages/Reports/shared/GridLayout';
import { formatWallClockWorded } from '@/utils/date';
import { getSwimSpeedUnitLabelByLocale, parseLocale } from '@/utils/localeFormatters';

import styles from './BehaviorSwimSpeed.module.scss';

interface BehaviorSwimSpeedViewProps {
    chart: UseMetricChartResult<'line'>;
    swimSpeedThreshold: ThresholdRange;
    lastMeasurement: string;
    isLoading: boolean;
    error?: unknown;
    /** Try again in the chart's error state. */
    retry?: RetryProps;
    /** Beside the legend: the radio group that picks the right axis. */
    legendRight?: React.ReactNode;
    dateFrom?: string;
    dateTo?: string;
    onDateChange?: (data: DateRange) => void;
}

export default function BehaviorSwimSpeedView(props: BehaviorSwimSpeedViewProps) {
    const {
        chart,
        lastMeasurement,
        swimSpeedThreshold,
        isLoading,
        error,
        retry,
        legendRight,
        dateFrom,
        dateTo,
        onDateChange,
    } = props;
    const { t, i18n } = useTranslation('behaviorModule');
    const locale = parseLocale(i18n.language);
    return (
        <GridLayout contentLayout="1-fixed" secondColumnWidth={375}>
            <ContentBlock
                title={t('swimSpeedView.title')}
                containerRef={chart.fullScreenContainerRef}
                headerActions={
                    <ChartHeaderActions
                        chartRef={chart.chartRef}
                        chartName="behavior-swim-speed-chart"
                        isFullScreen={chart.isFullScreen}
                        disabled={isLoading}
                        onFullScreenToggle={chart.handleFullScreenToggle}
                        dateFrom={dateFrom}
                        dateTo={dateTo}
                        onDateChange={onDateChange}
                        defaultPresetId="last7days"
                        show24hPreset={true}
                    />
                }>
                <MetricChart
                    legendRowLayout="centered"
                    kind="line"
                    data={chart.data}
                    options={chart.options}
                    legendItems={chart.legendItems}
                    visibleSeries={chart.visibleSeries}
                    onToggleSeries={chart.toggleSeries}
                    legendRight={legendRight}
                    chartRef={chart.chartRef}
                    height={400}
                    isLoading={isLoading}
                    error={error}
                    onRetry={retry?.onRetry}
                    isRetrying={retry?.isRetrying}
                />
            </ContentBlock>
            <ContentBlock title={t('common.infoLabel')} titleIcon={<TitleIcon icon={Info} />}>
                <div className={styles.infoTextContainer}>
                    {t('swimSpeedView.infoText', {
                        min: swimSpeedThreshold.min,
                        max: swimSpeedThreshold.max,
                        unit: getSwimSpeedUnitLabelByLocale(locale),
                    })}
                </div>
                <div>
                    <span className={styles.lastMeasurementLabel}>
                        <span>{t('common.lastMeasurement')}</span>
                        {isLoading ? (
                            <SkeletonBox width={100} height={24} borderRadius={8} />
                        ) : (
                            <span>{formatWallClockWorded(lastMeasurement)}</span>
                        )}
                    </span>
                </div>
            </ContentBlock>
        </GridLayout>
    );
}
